import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { AlimentoPersonaleDettaglio, Categoria, AlimentiFiltri, ApiResponse } from '../../../types/alimenti';
import Accordion from '../../controls/Accordion';
import PaginationControls from '../../controls/PaginationControls';
import NoResults from '../../controls/NoResults';
import SortIcon from '../../controls/SortIcon';
import useAccordion from '../../../hooks/useAccordion';
import usePagination from '../../../hooks/usePagination';
import useSort from '../../../hooks/useSort';
import './AlimentiPersonaliDettaglio.css';


const AlimentiPersonaliDettaglio: React.FC = () => {
  const navigate = useNavigate();
  const [risultati, setRisultati] = useState<AlimentoPersonaleDettaglio[]>([]);
  const [categorie, setCategorie] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Hook per ordinamento
  const { sortedData, sortConfig, handleSort } = useSort(risultati, {
    initialField: 'dataInserimento',
    initialDirection: 'desc'
  });
  
  // Hook per paginazione
  const {
    paginatedData,
    paginationInfo,
    goToPage,
    goToFirstPage,
    goToPreviousPage, 
    goToNextPage,
    goToLastPage,
    changeItemsPerPage,
    resetToFirstPage
  } = usePagination(sortedData, { initialItemsPerPage: 10 });
  
  // Stato per modal immagini
  const [modalImage, setModalImage] = useState<{src: string, alt: string, extraImages?: string[]} | null>(null);
  
  // Filtri
  const [filtri, setFiltri] = useState<AlimentiFiltri>({
    dataDa: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 giorni fa
    dataA: new Date().toISOString().split('T')[0], // oggi
    tipo: 'tutti',
    idCategoria: null
  });
  
  // Hook per accordion
  const filtersAccordion = useAccordion(true);

  // Carica categorie al mount
  useEffect(() => {
    caricaCategorie();
  }, []);

  // Gestione chiusura modal con ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && modalImage) {
        closeImageModal();
      }
    };

    if (modalImage) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Blocca scroll del body
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [modalImage]);

  const caricaCategorie = async () => {
    try {
      const response = await api.get<ApiResponse<Categoria[]>>('/AlimentiPersonali/categorie');
      if (response.data.success) {
        setCategorie(response.data.data);
      }
    } catch (error) {
      console.error('Errore nel caricamento categorie:', error);
    }
  };

  const cercaAlimenti = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const payload = {
        dataDa: filtri.dataDa,
        dataA: filtri.dataA,
        tipo: filtri.tipo,
        idCategoria: filtri.idCategoria || undefined
      };

      const response = await api.post<ApiResponse<AlimentoPersonaleDettaglio[]>>('/AlimentiPersonali/dettaglio', payload);
      
      if (response.data.success) {
        setRisultati(response.data.data);
        resetToFirstPage(); // Reset alla prima pagina per nuova ricerca
      } else {
        setError(response.data.message || 'Errore nella ricerca');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Errore durante la ricerca');
      console.error('Errore ricerca alimenti dettaglio:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (campo: keyof AlimentiFiltri, valore: any) => {
    setFiltri(prev => ({
      ...prev,
      [campo]: valore
    }));
  };


  // Funzione per formattare la data
  const formatData = (dataString: string) => {
    return new Date(dataString).toLocaleDateString('it-IT');
  };

  // Funzione per aprire il modal delle immagini
  const openImageModal = (mainImage: string, alimento: string, extraImages: string[] = []) => {
    setModalImage({
      src: mainImage,
      alt: alimento,
      extraImages: extraImages.filter(img => img && img !== mainImage)
    });
  };

  // Funzione per chiudere il modal
  const closeImageModal = () => {
    setModalImage(null);
  };

  // Funzione per mostrare l'immagine dell'alimento
  const AlimentoImmagine: React.FC<{ pic?: string; pic2?: string; alimento: string }> = ({ pic, pic2, alimento }) => {
    const images = [pic, pic2].filter(Boolean) as string[];
    
    if (images.length === 0) {
      return (
        <div className="alimento-immagine no-image">
          <span className="no-image-text">📷</span>
        </div>
      );
    }

    const mainImage = images[0];
    const extraImages = images.slice(1);

    return (
      <div className="alimento-immagine">
        <div className="image-container" onClick={() => openImageModal(mainImage, alimento, extraImages)}>
          <img 
            src={mainImage} 
            alt={alimento}
            className="img-alimento-small clickable"
            onError={(e) => {
              // Se l'immagine principale non carica, prova la seconda
              if (extraImages.length > 0) {
                (e.target as HTMLImageElement).src = extraImages[0];
              } else {
                // Mostra placeholder se nessuna immagine carica
                (e.target as HTMLImageElement).style.display = 'none';
                const container = (e.target as HTMLImageElement).parentElement;
                if (container) {
                  container.innerHTML = '<span class="no-image-text">❌</span>';
                }
              }
            }}
          />
          {images.length > 1 && (
            <div className="image-count-badge">
              {images.length}
            </div>
          )}
          <div className="image-overlay">
            <span className="zoom-icon">🔍</span>
          </div>
        </div>
      </div>
    );
  };

  // Componente Modal per visualizzare le immagini
  const ImageModal: React.FC = () => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    
    if (!modalImage) return null;

    const allImages = [modalImage.src, ...(modalImage.extraImages || [])];

    const nextImage = () => {
      setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
    };

    const prevImage = () => {
      setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
    };

    return (
      <div className="image-modal-overlay" onClick={closeImageModal}>
        <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={closeImageModal}>
            ✕
          </button>
          
          <div className="modal-image-container">
            <img 
              src={allImages[currentImageIndex]} 
              alt={modalImage.alt}
              className="modal-image"
            />
            
            {allImages.length > 1 && (
              <>
                <button className="modal-nav modal-prev" onClick={prevImage}>
                  ‹
                </button>
                <button className="modal-nav modal-next" onClick={nextImage}>
                  ›
                </button>
                <div className="modal-image-counter">
                  {currentImageIndex + 1} / {allImages.length}
                </div>
              </>
            )}
          </div>
          
          <div className="modal-image-info">
            <h3>{modalImage.alt}</h3>
            {allImages.length > 1 && (
              <p>Immagine {currentImageIndex + 1} di {allImages.length}</p>
            )}
          </div>
        </div>
      </div>
    );
  };


  return (
    <div className="alimenti-personali-container">
      <div className="page-header">
        <div className="header-top">
          <button 
            className="btn-back" 
            onClick={() => navigate('/alimenti-personali')}
            title="Torna alla vista Conteggio"
          >
            ← Conteggio
          </button>
          <button 
            className="btn-back secondary" 
            onClick={() => navigate('/')}
            title="Torna alla Dashboard"
          >
            🏠 Dashboard
          </button>
        </div>
        <div className="header-content-main">
          <div className="header-text">
            <h1>Dettaglio Alimenti Personali</h1>
            <p className="page-description">
              Analisi dettagliata degli alimenti inseriti dagli utenti con informazioni nutrizionali complete
            </p>
          </div>
          
          <div className="header-actions">
            <button 
              className="btn-header-action" 
              onClick={() => navigate('/alimenti-personali')}
              title="Vai alla vista conteggio"
            >
              📊 Conteggio
            </button>
            
            <button 
              className="btn-header-action" 
              onClick={() => navigate('/alimenti-personali/dettaglio')}
              disabled={true}
              title="Sei già nella vista Dettaglio"
            >
              🔍 Dettaglio
            </button>
          </div>
        </div>
      </div>

      {/* Sezione Filtri */}
      <div className="filtri-section">
        <Accordion 
          title="Filtri di Ricerca"
          isOpen={filtersAccordion.isOpen}
          onToggle={filtersAccordion.toggle}
        >
          <div className="filtri-grid">
            <div className="filtro-item">
              <label htmlFor="dataDa">Data Da:</label>
              <input
                type="date"
                id="dataDa"
                value={filtri.dataDa}
                onChange={(e) => handleFiltroChange('dataDa', e.target.value)}
              />
            </div>

            <div className="filtro-item">
              <label htmlFor="dataA">Data A:</label>
              <input
                type="date"
                id="dataA"
                value={filtri.dataA}
                onChange={(e) => handleFiltroChange('dataA', e.target.value)}
              />
            </div>

            <div className="filtro-item">
              <label htmlFor="categoria">Categoria:</label>
              <select
                id="categoria"
                value={filtri.idCategoria || ''}
                onChange={(e) => handleFiltroChange('idCategoria', e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">Tutte le categorie</option>
                {categorie.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.descrizione}</option>
                ))}
              </select>
            </div>

            <div className="filtro-item">
              <label htmlFor="tipo">Tipo:</label>
              <select
                id="tipo"
                value={filtri.tipo}
                onChange={(e) => handleFiltroChange('tipo', e.target.value)}
              >
                <option value="tutti">Tutti</option>
                <option value="piatti">Piatti</option>
                <option value="alimenti">Alimenti</option>
              </select>
            </div>
          </div>

          <div className="filtri-actions">
            <button 
              className="btn-primary" 
              onClick={cercaAlimenti}
              disabled={loading}
            >
              {loading ? 'Ricerca in corso...' : 'Cerca'}
            </button>
          </div>
        </Accordion>
      </div>

      {/* Messaggio di errore */}
      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
        </div>
      )}

      {/* Tabella Risultati */}
      <div className="risultati-section">
        <h2>Dettagli Alimenti</h2>
        
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Caricamento dati...</p>
          </div>
        )}

        {!loading && risultati.length === 0 && !error && (
          <NoResults />
        )}

        {!loading && risultati.length > 0 && (
          <>
            <PaginationControls
              paginationInfo={paginationInfo}
              onGoToPage={goToPage}
              onGoToFirstPage={goToFirstPage}
              onGoToPreviousPage={goToPreviousPage}
              onGoToNextPage={goToNextPage}
              onGoToLastPage={goToLastPage}
              onChangeItemsPerPage={changeItemsPerPage}
              showBottomControls={false}
            />

            <div className="table-container">
              <table className="risultati-table dettaglio" style={{tableLayout: 'fixed', width: '100%', minWidth: '1400px'}}>
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleSort('username')} style={{width: '120px'}}>
                      Username <SortIcon field="username" sortConfig={sortConfig} />
                    </th>
                    <th style={{width: '80px', textAlign: 'center'}}>Immagine</th>
                    <th className="sortable" onClick={() => handleSort('alimento')} style={{width: '250px'}}>
                      Alimento <SortIcon field="alimento" sortConfig={sortConfig} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('categoria')} style={{width: '180px'}}>
                      Categoria <SortIcon field="categoria" sortConfig={sortConfig} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('produttore')} style={{width: '140px'}}>
                      Produttore <SortIcon field="produttore" sortConfig={sortConfig} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('kcal')} style={{width: '80px', textAlign: 'center'}}>
                      Kcal <SortIcon field="kcal" sortConfig={sortConfig} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('dataInserimento')} style={{width: '100px', textAlign: 'center'}}>
                      Data <SortIcon field="dataInserimento" sortConfig={sortConfig} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('proteine')} style={{width: '90px', textAlign: 'center'}}>
                      Proteine <SortIcon field="proteine" sortConfig={sortConfig} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('carboidrati')} style={{width: '90px', textAlign: 'center'}}>
                      Carboidrati <SortIcon field="carboidrati" sortConfig={sortConfig} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('lipidi')} style={{width: '90px', textAlign: 'center'}}>
                      Lipidi <SortIcon field="lipidi" sortConfig={sortConfig} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('fibra')} style={{width: '90px', textAlign: 'center'}}>
                      Fibre <SortIcon field="fibra" sortConfig={sortConfig} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((item, index) => (
                    <tr key={`${item.idAlimento}-${item.idUser}-${index}`}>
                      <td className="username" style={{width: '120px', padding: '8px', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                        {item.username}
                      </td>
                      <td className="immagine" style={{width: '80px', textAlign: 'center', padding: '8px'}}>
                        <AlimentoImmagine 
                          pic={item.pic} 
                          pic2={item.pic2} 
                          alimento={item.alimento} 
                        />
                      </td>
                      <td className="alimento" style={{width: '250px', padding: '8px', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                        <div className="alimento-info">
                          <strong>{item.alimento}</strong>
                          <small className="tipo-badge">{item.tipo}</small>
                        </div>
                      </td>
                      <td className="categoria" style={{width: '180px', padding: '8px', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                        {item.categoria}
                      </td>
                      <td className="produttore" style={{width: '140px', padding: '8px', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                        {item.produttore || '-'}
                      </td>
                      <td className="kcal" style={{width: '80px', textAlign: 'center', padding: '8px'}}>
                        {item.kcal.toFixed(0)}
                      </td>
                      <td className="data" style={{width: '100px', textAlign: 'center', padding: '8px'}}>
                        {formatData(item.dataInserimento)}
                      </td>
                      <td className="nutriente" style={{width: '90px', textAlign: 'center', padding: '8px'}}>
                        {item.proteine.toFixed(1)}g
                      </td>
                      <td className="nutriente" style={{width: '90px', textAlign: 'center', padding: '8px'}}>
                        {item.carboidrati.toFixed(1)}g
                      </td>
                      <td className="nutriente" style={{width: '90px', textAlign: 'center', padding: '8px'}}>
                        {item.lipidi.toFixed(1)}g
                      </td>
                      <td className="nutriente" style={{width: '90px', textAlign: 'center', padding: '8px'}}>
                        {item.fibra.toFixed(1)}g
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <PaginationControls
              paginationInfo={paginationInfo}
              onGoToPage={goToPage}
              onGoToFirstPage={goToFirstPage}
              onGoToPreviousPage={goToPreviousPage}
              onGoToNextPage={goToNextPage}
              onGoToLastPage={goToLastPage}
              onChangeItemsPerPage={changeItemsPerPage}
              showTopControls={false}
            />
          </>
        )}
      </div>

      {/* Modal per visualizzare le immagini */}
      <ImageModal />
    </div>
  );
};

export default AlimentiPersonaliDettaglio;